import type { ReactElement, ReactNode } from 'react'
import { useEffect, useRef } from 'react'

/**
 * The product's modal: 576px, radius 20, over a scrim.
 *
 * The native `dialog` element shown with `showModal`, so the page behind it is
 * inert, Escape closes it and focus cannot wander out — without a dependency,
 * and matching what `Manual.tsx` already does rather than inventing a second
 * way to be modal in the same product.
 */
export function Modal({
  title,
  lede,
  children,
  footer,
  onClose,
  labelledBy = 'nw-modal-title',
  width,
}: {
  title: string
  lede?: string
  children: ReactNode
  footer?: ReactNode
  onClose: () => void
  labelledBy?: string
  /**
   * `wide` is the two-column dialog: a guide to somebody else's interface
   * beside the box it ends in. At 576 the guide is a column of four words.
   * What it looks like is the caller's, so only the name is here.
   */
  width?: 'wide'
}): ReactElement {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    // jsdom ships the element with no behaviour at all, so the call is guarded
    // rather than assumed.
    ref.current?.showModal?.()
  }, [])

  return (
    <dialog className="nw-modal" data-width={width} ref={ref} aria-labelledby={labelledBy} onClose={onClose}>
      <div className="nw-modal-head">
        <div>
          <h2 id={labelledBy}>{title}</h2>
          {lede !== undefined && <p>{lede}</p>}
        </div>
        <button type="button" className="nw-modal-close" aria-label="Close" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="5" y1="5" x2="19" y2="19" />
            <line x1="19" y1="5" x2="5" y2="19" />
          </svg>
        </button>
      </div>
      <div className="nw-modal-body">{children}</div>
      {footer !== undefined && <div className="nw-modal-foot">{footer}</div>}
    </dialog>
  )
}

/** A label over a 46px control, which is the only field shape in the system. */
export function Field({
  id,
  label,
  children,
  width,
}: {
  id: string
  label: string
  children: ReactNode
  /** `narrow` is the units box; `set` is the one that grows to fill the row. */
  width?: 'narrow' | 'set'
}): ReactElement {
  return (
    <div className="nw-field" data-width={width}>
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  )
}

/** A field whose unit sits inside it, to the right of what is typed. */
export function UnitField({
  id,
  label,
  unit,
  children,
}: {
  id: string
  label: string
  unit: string
  children: ReactNode
}): ReactElement {
  return (
    <Field id={id} label={label} width="set">
      <span className="nw-unit-field">
        {children}
        <span>{unit}</span>
      </span>
    </Field>
  )
}

/**
 * What the figure on the page behind will become, and that nothing has happened
 * yet.
 *
 * A number this product is about is worth stating before it is changed rather
 * than after, and the sentence that says nothing is saved yet is the whole
 * reason a person will press the button.
 */
export function ConfirmPanel({
  label,
  caveat,
  children,
}: {
  label: string
  caveat: string
  children: ReactNode
}): ReactElement {
  return (
    <div className="nw-preview">
      <span>
        <span className="nw-preview-label">{label}</span>
        <span className="nw-preview-caveat">{caveat}</span>
      </span>
      <span className="nw-preview-value">{children}</span>
    </div>
  )
}
